import * as Haptics from 'expo-haptics';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { APP_MODAL_ROUTES, LOCATION_MANAGER_ROUTES } from '@/navigators/routes';
import { createStyles } from '@/contexts/theme-context';
import { fennelTrack } from '@/analytics/fennel/hook';
import { getTranslationValue } from '@/utils/translations';
import { HubCardBase } from '@/components/insights-hub/hub-card-base';
import { HubLowestPerformingCustomersSkeleton } from '@/components/insights-hub/hub-lowest-performing-customer-skeleton';
import { LocationManagerStackParams } from '@/navigators/types';
import {
  LowestPerformingCustomerDataType,
  LowestPerformingCustomersHubCardDataItem,
} from '@/types';
import { LowestPerformingCustomersCardMode } from '@/enums';
import { Platform, View } from 'react-native';
import { RestaurantInfoHeader } from '@/components/insights-hub/restaurant-info-header';
import { StackNavigationProp } from '@react-navigation/stack';
import { toFixedDecimalPercentage } from '@/utils/percentage';
import { Toggle } from '@usepepper/ui/atoms/toggle';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLowestPerformingCustomersData } from '@/hooks/insights-hub/use-lowest-performing-customers-data';
import { useNavigation } from '@react-navigation/native';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';

type Navigation = StackNavigationProp<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.ViewAccount
>;

export const HubLowestPerformingCustomers: React.FC = () => {
  const [styles, theme] = useStyles();

  const { navigate } = useNavigation<Navigation>();
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );
  const { data, loading, error, gpPercentageCustomers, unitsSoldCustomers } =
    useLowestPerformingCustomersData();

  const [selectedCardMode, setSelectedCardMode] =
    useState<LowestPerformingCustomersCardMode>(
      showGrossProfit
        ? LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE
        : LowestPerformingCustomersCardMode.UNITS_SOLD,
    );

  const selectedLowestPerformingCustomers = useMemo(() => {
    if (
      selectedCardMode === LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE
    ) {
      return gpPercentageCustomers;
    }

    return unitsSoldCustomers;
  }, [selectedCardMode, gpPercentageCustomers, unitsSoldCustomers]);

  const description = useMemo(() => {
    return selectedCardMode ===
      LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE
      ? i18n.t('insights_hub.lowest_performing_customers.gp_percentage')
      : i18n.t('insights_hub.lowest_performing_customers.units_sold');
  }, [selectedCardMode]);

  useEffect(() => {
    if (loading) {
      return;
    }

    // if GP is hidden or there are no GP% percentage customers, set the selected card mode to units sold
    if (!showGrossProfit || gpPercentageCustomers.items.length < 1) {
      setSelectedCardMode(LowestPerformingCustomersCardMode.UNITS_SOLD);
    }
  }, [loading, gpPercentageCustomers, showGrossProfit]);

  // cache policy is cache-and-network,
  //   loading will be true while cache data is displayed and network data is being retrieved
  if (loading && !data) {
    return Platform.OS === 'web' ? (
      <HubLowestPerformingCustomersSkeleton />
    ) : null;
  }

  if (!data || error) {
    return null;
  }

  if (
    unitsSoldCustomers.items.length < 1 &&
    (!showGrossProfit || gpPercentageCustomers.items.length < 1)
  ) {
    return null;
  }

  const getFormattedValue = (
    restaurant: LowestPerformingCustomersHubCardDataItem,
  ) => {
    if (restaurant.type === LowestPerformingCustomerDataType.Percentage) {
      return toFixedDecimalPercentage(restaurant.value);
    } else if (restaurant.type === LowestPerformingCustomerDataType.Unit) {
      return restaurant.value.toFixed(0);
    }

    return restaurant.value;
  };

  const onChangeToggle = (value: LowestPerformingCustomersCardMode) => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    setSelectedCardMode(value);

    fennelTrack(AnalyticEvent.LOWEST_PERFORMING_CUSTOMERS_TOGGLE_PRESS, {
      selectedCardMode: value,
    });
  };

  const onNavigateToViewAccount = (
    restaurant: LowestPerformingCustomersHubCardDataItem,
  ) => {
    navigate(APP_MODAL_ROUTES.LocationManager, {
      params: {
        restaurantName: getTranslationValue(restaurant.restaurant_name),
        restaurantUUID: restaurant.restaurant_uuid,
        supplierUUID: restaurant.supplier_uuid,
      },
      screen: LOCATION_MANAGER_ROUTES.ViewAccount,
    });
  };

  return (
    <HubCardBase
      actionsElement={
        <View style={styles.toggle}>
          {gpPercentageCustomers.items.length > 0 && showGrossProfit ? (
            <Toggle<LowestPerformingCustomersCardMode>
              onPress={onChangeToggle}
              selectedTab={selectedCardMode}
              tabs={[
                {
                  label: i18n.t(
                    'insights_hub.lowest_performing_customers.toggle.gp_percentage',
                  ),
                  value: LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE,
                },
                {
                  label: i18n.t(
                    'insights_hub.lowest_performing_customers.toggle.units_sold',
                  ),
                  value: LowestPerformingCustomersCardMode.UNITS_SOLD,
                },
              ]}
            />
          ) : null}
        </View>
      }
      childrenStyle={styles.hubCardContentContainer}
      description={description}
      title={getTranslationValue(data.title)}
    >
      {selectedLowestPerformingCustomers.items.map((restaurant) => (
        <View key={restaurant.restaurant_uuid} style={styles.row}>
          <View style={styles.flex}>
            <RestaurantInfoHeader
              onPress={() => onNavigateToViewAccount(restaurant)}
              restaurantUUID={restaurant.restaurant_uuid}
              rightElement={
                <Typography
                  color={theme.sk.color.text.primary}
                  numberOfLines={1}
                  variant="paragraph-base"
                >
                  {getFormattedValue(restaurant)}
                </Typography>
              }
              subtitle={restaurant.subtitle}
              title={restaurant.restaurant_name}
            />
          </View>
        </View>
      ))}
    </HubCardBase>
  );
};

const useStyles = createStyles(() => ({
  flex: {
    flex: 1,
  },
  hubCardContentContainer: {
    gap: 16,
    paddingTop: 16,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  toggle: {
    width: 100,
  },
}));
